import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import Icon from '../AppIcon';

/**
 * LeaderboardWidget Component
 * Displays a game leaderboard with realtime rank updates
 *
 * Usage:
 * <LeaderboardWidget
 *   gameId="game_uuid"
 *   leaderboardKey="high_scores"
 *   scope="global"
 *   limit={10}
 *   showUserRank={true}
 * />
 */
export default function LeaderboardWidget({
  gameId,
  leaderboardKey,
  scope = 'global',
  limit = 10,
  showUserRank = true,
  className = ''
}) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [nearbyEntries, setNearbyEntries] = useState([]);
  const [error, setError] = useState(null);
  const [selectedScope, setSelectedScope] = useState(scope);
  const realtimeChannelRef = useRef(null);

  useEffect(() => {
    if (gameId && leaderboardKey) {
      loadLeaderboard();
      if (showUserRank && user?.id) {
        loadUserRank();
      }
    }

    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
      }
    };
  }, [gameId, leaderboardKey, selectedScope, user?.id]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get leaderboard entries
      const { data: leaderboardData, error: leaderboardError } = await supabase
        .from('leaderboard_entries')
        .select(`
          id,
          score,
          rank,
          submitted_at,
          metadata,
          user:user_id (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('leaderboard_id', await getLeaderboardId())
        .order('rank', { ascending: true })
        .limit(limit);

      if (leaderboardError) throw leaderboardError;

      setEntries(leaderboardData || []);

      // Set up realtime subscription
      setupRealtimeSubscription();

    } catch (err) {
      console.error('Error loading leaderboard:', err);
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const loadUserRank = async () => {
    if (!user?.id) return;

    try {
      const leaderboardId = await getLeaderboardId();

      const { data, error } = await supabase
        .from('leaderboard_entries')
        .select('id, score, rank, submitted_at')
        .eq('leaderboard_id', leaderboardId)
        .eq('user_id', user.id)
        .order('score', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      setUserRank(data);

      // Load nearby entries if user has a rank
      if (data?.rank) {
        const { data: nearby, error: nearbyError } = await supabase
          .from('leaderboard_entries')
          .select(`
            id,
            score,
            rank,
            submitted_at,
            user:user_id (
              id,
              full_name,
              avatar_url
            )
          `)
          .eq('leaderboard_id', leaderboardId)
          .gte('rank', data.rank - 2)
          .lte('rank', data.rank + 2)
          .order('rank', { ascending: true });

        if (!nearbyError) {
          setNearbyEntries(nearby || []);
        }
      }
    } catch (err) {
      console.error('Error loading user rank:', err);
    }
  };

  const getLeaderboardId = async () => {
    // Get or create leaderboard
    const { data, error } = await supabase
      .from('leaderboards')
      .select('id')
      .eq('game_id', gameId)
      .eq('leaderboard_key', leaderboardKey)
      .single();

    if (error) throw error;
    return data.id;
  };

  const setupRealtimeSubscription = async () => {
    const leaderboardId = await getLeaderboardId();

    const channel = supabase
      .channel(`leaderboard_${leaderboardId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leaderboard_entries',
          filter: `leaderboard_id=eq.${leaderboardId}`
        },
        (payload) => {
          // Reload leaderboard on any change
          loadLeaderboard();
          if (showUserRank && user?.id) {
            loadUserRank();
          }
        }
      )
      .subscribe();

    realtimeChannelRef.current = channel;
  };

  const formatScore = (score) => {
    return Number(score).toLocaleString();
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const scopeOptions = [
    { value: 'global', label: 'All Time' },
    { value: 'daily', label: 'Today' },
    { value: 'weekly', label: 'This Week' },
    { value: 'monthly', label: 'This Month' }
  ];

  if (loading) {
    return (
      <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading leaderboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <p className="text-red-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Trophy" size={20} className="text-primary" />
          <h3 className="font-semibold text-foreground">Leaderboard</h3>
        </div>

        {/* Scope selector */}
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          {scopeOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setSelectedScope(option.value)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                selectedScope === option.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* User's rank (if applicable) */}
      {showUserRank && userRank && (
        <div className="p-4 bg-primary/5 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-2xl font-bold text-primary">
                {getRankBadge(userRank.rank)}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Your Rank</p>
                <p className="text-xs text-muted-foreground">Score: {formatScore(userRank.score)}</p>
              </div>
            </div>
            <button
              onClick={loadUserRank}
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              <Icon name="RefreshCw" size={12} />
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Leaderboard entries */}
      <div className="divide-y divide-border">
        {entries.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Trophy" size={48} className="text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No scores yet. Be the first!</p>
          </div>
        ) : (
          entries.map((entry, index) => {
            const isCurrentUser = user?.id === entry.user?.id;

            return (
              <div
                key={entry.id}
                className={`p-3 flex items-center gap-3 hover:bg-muted/50 transition-colors ${
                  isCurrentUser ? 'bg-primary/5' : ''
                }`}
              >
                {/* Rank */}
                <div className={`w-10 text-center font-bold ${
                  entry.rank <= 3 ? 'text-2xl' : 'text-lg text-muted-foreground'
                }`}>
                  {getRankBadge(entry.rank)}
                </div>

                {/* Avatar */}
                <img
                  src={entry.user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(entry.user?.full_name || 'Unknown')}&background=6366f1&color=fff`}
                  alt={entry.user?.full_name}
                  className="w-10 h-10 rounded-full"
                />

                {/* User info */}
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm truncate ${
                    isCurrentUser ? 'text-primary' : 'text-foreground'
                  }`}>
                    {entry.user?.full_name || 'Anonymous'}
                    {isCurrentUser && <span className="text-xs ml-2">(You)</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatTimeAgo(entry.submitted_at)}
                  </p>
                </div>

                {/* Score */}
                <div className="text-right">
                  <p className="font-bold text-foreground">{formatScore(entry.score)}</p>
                  {entry.metadata?.level && (
                    <p className="text-xs text-muted-foreground">Level {entry.metadata.level}</p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Nearby ranks section */}
      {showUserRank && nearbyEntries.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <p className="text-xs font-semibold text-muted-foreground mb-2">NEARBY RANKS</p>
          <div className="space-y-2">
            {nearbyEntries.map(entry => {
              const isCurrentUser = user?.id === entry.user?.id;
              return (
                <div
                  key={entry.id}
                  className={`flex items-center gap-2 text-sm ${
                    isCurrentUser ? 'font-semibold text-primary' : 'text-muted-foreground'
                  }`}
                >
                  <span className="w-8">{getRankBadge(entry.rank)}</span>
                  <span className="flex-1 truncate">{entry.user?.full_name}</span>
                  <span>{formatScore(entry.score)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
